import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Warehouse } from 'lucide-react';
import { useFormat } from '@/hooks/useAccess';
import { useStockValuationQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatMoney, formatNumber, formatQuantity } from '@/lib/format';
import {
  Card, CardBody, CardHeader, EmptyState, ErrorState, LoadingState, PageHeader,
  Table, Td, Th,
} from '@/components/ui';

// A qualitative sequence: distinguishable in both themes and to most viewers
// with colour vision deficiency.
const SLICE_COLORS = ['#4f7fd8', '#54b399', '#d9a441', '#c1666b', '#8a6bbf', '#5b9bd5'];

export function ValuationPage() {
  const format = useFormat();
  const { data, isLoading, isError, error, refetch } = useStockValuationQuery();

  if (isLoading) return <LoadingState label="Calculating inventory value…" />;
  if (isError) return <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />;

  const slices =
    data?.byWarehouse.map((entry, index) => ({
      name: entry.warehouseName,
      value: Number.parseFloat(entry.totalValue),
      fill: SLICE_COLORS[index % SLICE_COLORS.length],
    })) ?? [];

  const hasValue = slices.some((slice) => slice.value > 0);

  return (
    <>
      <PageHeader
        title="Inventory valuation"
        description="What your stock is worth at moving-average cost, warehouse by warehouse."
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Total value" />
          <CardBody>
            <p className="text-3xl font-semibold tabular">{formatMoney(data?.totalValue, format)}</p>
            <p className="mt-1 text-sm text-muted">
              Across {formatNumber(data?.byWarehouse.length ?? 0, format.locale)} warehouse(s)
            </p>

            {hasValue && (
              <div className="mt-6 h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={slices}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {slices.map((slice) => (
                        <Cell key={slice.name} fill={slice.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => formatMoney(value, format)}
                      contentStyle={{
                        background: 'var(--surface-raised)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="By warehouse" />
          {(data?.byWarehouse.length ?? 0) === 0 ? (
            <EmptyState
              icon={Warehouse}
              title="Nothing to value yet"
              description="Post opening balances or receive a purchase to start building a valuation."
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Warehouse</Th>
                  <Th numeric>Distinct items</Th>
                  <Th numeric>Total units</Th>
                  <Th numeric>Value</Th>
                </tr>
              </thead>
              <tbody>
                {data?.byWarehouse.map((entry, index) => (
                  <tr key={entry.warehouseId}>
                    <Td>
                      <span className="flex items-center gap-2">
                        <span
                          className="size-2.5 shrink-0 rounded-full"
                          style={{ background: SLICE_COLORS[index % SLICE_COLORS.length] }}
                          aria-hidden
                        />
                        {entry.warehouseName}
                      </span>
                    </Td>
                    <Td numeric className="text-secondary">
                      {formatNumber(entry.skuCount, format.locale)}
                    </Td>
                    <Td numeric className="text-secondary">
                      {formatQuantity(entry.totalQuantity, format)}
                    </Td>
                    <Td numeric className="font-medium">{formatMoney(entry.totalValue, format)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    </>
  );
}
